// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { IApplicationInsights } from '@microsoft/applicationinsights-web';
import { BotServerAppInsights, BotServerAppInsightsEvent } from './BotServerAppInsights';
import { v4 as uuid } from 'uuid';

export async function fetchWithDetailedTiming<T extends IApplicationInsights | BotServerAppInsights>(
  url: string,
  options: RequestInit = {},
  appInsights?: T,
  telemetryEventName?: T extends IApplicationInsights ? string : BotServerAppInsightsEvent
): Promise<Response> {
  const response = await fetch(url, options);

  // Wait for performance entries to be available
  setTimeout(() => {
    const entries = performance.getEntriesByName(url);
    if (entries.length > 0) {
      const entry = entries[0] as PerformanceResourceTiming; // Latest timing for given url

      appInsights?.trackDependencyData({
        id: uuid(),
        target: new URL(url).host,
        name: telemetryEventName as BotServerAppInsightsEvent,
        duration: entry.duration,
        success: response.ok,
        type: 'Fetch',
        responseCode: response.status,
        properties: {
          dnsLookup: entry.domainLookupEnd - entry.domainLookupStart,
          tcpHandshake: entry.connectEnd - entry.connectStart,
          tlsTime: entry.secureConnectionStart ? entry.connectEnd - entry.secureConnectionStart : 0,
          requestSent: entry.responseStart - entry.requestStart,
          responseTime: entry.responseEnd - entry.responseStart,
          totalTime: entry.duration
        }
      });
    }
  }, 0); // Use next event loop tick to allow performance entry to be recorded

  return response;
}
